import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import SprintAPIService from '../services/SprintAPIService';

const SessionContext = createContext();

export const useSessionContext = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSessionContext must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  // Initialize state from localStorage
  const [activeSession, setActiveSession] = useState(() => {
    const stored = localStorage.getItem('activeSession');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Check if session is still valid (not older than 24 hours)
        if (parsed.startTime && Date.now() - parsed.startTime < 24 * 60 * 60 * 1000) {
          return parsed;
        }
      } catch (e) {
        console.error('Failed to parse stored session:', e);
      }
    }
    return null;
  });

  const [sessionTimer, setSessionTimer] = useState(() => {
    const stored = localStorage.getItem('sessionTimer');
    return stored ? parseInt(stored, 10) : 1800; // Default 30 min
  });

  const [isPaused, setIsPaused] = useState(() => {
    const stored = localStorage.getItem('sessionPaused');
    return stored === 'true';
  });

  // Break reminder states
  const [workIntervalElapsed, setWorkIntervalElapsed] = useState(0);
  const [totalSessionTime, setTotalSessionTime] = useState(0);
  const [shouldShowBreakReminder, setShouldShowBreakReminder] = useState(false);
  const [breaksCount, setBreaksCount] = useState(0);
  const [lastBreakTime, setLastBreakTime] = useState(null);

  const BREAK_INTERVAL = 30 * 60; // 30 minutes in seconds

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (activeSession) {
      localStorage.setItem('activeSession', JSON.stringify(activeSession));
    } else {
      localStorage.removeItem('activeSession');
    }
  }, [activeSession]);

  useEffect(() => {
    localStorage.setItem('sessionTimer', sessionTimer.toString());
  }, [sessionTimer]);

  useEffect(() => {
    localStorage.setItem('sessionPaused', isPaused.toString());
  }, [isPaused]);

  // Start a new session
  const startSession = useCallback((sessionData) => {
    const totalSeconds = sessionData.estimatedMinutes * 60 || 1800;
    const newSession = {
      ...sessionData,
      startTime: Date.now(),
      totalTime: totalSeconds, // Store total time for display
      tasks: sessionData.tasks?.map(task => ({
        ...task,
        isCompleted: task.isCompleted || false
      })) || []
    };
    setActiveSession(newSession);
    setSessionTimer(totalSeconds);
    setTotalSessionTime(totalSeconds);
    setIsPaused(false);
    setWorkIntervalElapsed(0);
    setShouldShowBreakReminder(false);
    setBreaksCount(0);
    setLastBreakTime(null);
  }, []);

  // Update task completion status
  const toggleTaskCompletion = useCallback(async (taskId) => {
    if (!activeSession) return;

    const task = activeSession.tasks.find(t => t.id === taskId);
    if (!task) return;

    const newCompletedState = !task.isCompleted;

    // Update local state immediately for responsive UI
    const updatedSession = {
      ...activeSession,
      tasks: activeSession.tasks.map(t =>
        t.id === taskId
          ? { ...t, isCompleted: newCompletedState }
          : t
      )
    };
    setActiveSession(updatedSession);

    // If this is a sprint task, update it in the database
    if (activeSession.sprintId && activeSession.userId) {
      try {
        const status = newCompletedState ? 'COMPLETE' : 'NOT_STARTED';
        await SprintAPIService.updateTaskStatus(
          activeSession.userId,
          activeSession.sprintId,
          taskId,
          status
        );
        console.log('[SessionContext] Task status updated in database:', taskId, status);

        // Dispatch event to notify SprintDashboard to refresh
        window.dispatchEvent(new CustomEvent('sprintStatusChanged', {
          detail: {
            sprintId: activeSession.sprintId,
            taskId,
            status,
            reason: 'task_completion'
          }
        }));
        console.log('[SessionContext] ✅ Task completion event dispatched');
      } catch (error) {
        console.error('[SessionContext] Error updating task status:', error);
        // Don't revert local state - let user continue working
      }
    }
  }, [activeSession]);

  // Close the session
  const closeSession = useCallback(async () => {
    console.log('[SessionContext] closeSession called, activeSession:', activeSession);

    // If there's an active sprint, pause it in the database
    if (activeSession?.sprintId) {
      console.log('[SessionContext] Attempting to pause sprint:', activeSession.sprintId, 'userId:', activeSession.userId);
      try {
        // Get userId from activeSession or use a stored value
        const userId = activeSession.userId;
        if (userId) {
          console.log('[SessionContext] Calling pauseSprint API...');
          await SprintAPIService.pauseSprint(userId, activeSession.sprintId);
          console.log('[SessionContext] ✅ Sprint paused in database:', activeSession.sprintId);

          // Dispatch a custom event to notify SprintDashboard to refresh
          window.dispatchEvent(new CustomEvent('sprintStatusChanged', {
            detail: { sprintId: activeSession.sprintId, status: 'PAUSED' }
          }));
          console.log('[SessionContext] ✅ Event dispatched');
        } else {
          console.warn('[SessionContext] ⚠️ No userId found in activeSession');
        }
      } catch (error) {
        console.error('[SessionContext] ❌ Error pausing sprint:', error);
        // Don't block session close if sprint pause fails
      }
    } else {
      console.log('[SessionContext] No active sprint to pause');
    }

    setActiveSession(null);
    setSessionTimer(1800);
    setIsPaused(false);
    localStorage.removeItem('activeSession');
    localStorage.removeItem('sessionTimer');
    localStorage.removeItem('sessionPaused');
    console.log('[SessionContext] Session closed and cleared');
  }, [activeSession]);

  // Pause/resume timer
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  // Update remaining time and check for break reminder
  const updateTimer = useCallback((newTime) => {
    setSessionTimer(newTime);

    // Only increment work interval if not paused
    if (!isPaused && activeSession) {
      setWorkIntervalElapsed(prev => {
        const newInterval = prev + 1;
        // Check if it's time for a break (every 30 minutes)
        if (newInterval >= BREAK_INTERVAL && newInterval % BREAK_INTERVAL === 0) {
          setShouldShowBreakReminder(true);
        }
        return newInterval;
      });
    }
  }, [isPaused, activeSession, BREAK_INTERVAL]);

  // Take a break
  const takeBreak = useCallback(() => {
    setIsPaused(true);
    setShouldShowBreakReminder(false);
    setBreaksCount(prev => prev + 1);
    setLastBreakTime(Date.now());
    setWorkIntervalElapsed(0); // Reset work interval
  }, []);

  // Dismiss break reminder
  const dismissBreakReminder = useCallback(() => {
    setShouldShowBreakReminder(false);
  }, []);

  const value = useMemo(() => ({
    activeSession,
    sessionTimer,
    totalSessionTime,
    isPaused,
    workIntervalElapsed,
    shouldShowBreakReminder,
    breaksCount,
    startSession,
    toggleTaskCompletion,
    closeSession,
    togglePause,
    updateTimer,
    takeBreak,
    dismissBreakReminder
  }), [
    activeSession,
    sessionTimer,
    totalSessionTime,
    isPaused,
    workIntervalElapsed,
    shouldShowBreakReminder,
    breaksCount,
    startSession,
    toggleTaskCompletion,
    closeSession,
    togglePause,
    updateTimer,
    takeBreak,
    dismissBreakReminder
  ]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};